import React, { useEffect } from "react";
import useSWR from "swr";
import {
  Box,
  Card,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import useCommon from "../../hooks/useCommon";
import { useFetcher } from "../../hooks/useApi";
import SubscribersRow from "./SubscribersRow";
import { Subscriber } from "./Subscribers.type";

const valueKey: { [key: string]: string } = {
  premium_subscriptions_month_canceled_count: "Month Canceled (Active)",
  premium_subscriptions_year_canceled_count: "Year Canceled (Active)",
  premium_subscriptions_month_count: "Month Active",
  premium_subscriptions_year_count: "Year Active",
};

const Subscribers = () => {
  const fetcher = useFetcher();
  const { setBreadCrumbs } = useCommon();
  const theme = useTheme();

  useEffect(() => {
    setBreadCrumbs?.([{ label: "Subscribers" }]);
  }, [setBreadCrumbs]);

  const { data } = useSWR(["/user-info/subscriptions"], fetcher);

  const skeletonArray = Array(40).fill(Array(4).fill(""));

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      />
      <Card>
        <Toolbar>
          {data?.dashboard ? (
            <Table
              sx={{
                border: `1px solid rgba(224, 224, 224, 1)`,
                borderRadius: "5px",
                marginBottom: "25px",
              }}
              size="small"
              aria-label="purchases"
            >
              <TableHead>
                <TableRow sx={{ background: theme.palette.action.hover }}>
                  {Object.keys(data.dashboard).map(
                    (item: string, index: number) => (
                      <TableCell key={index}>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                          }}
                          variant="body1"
                        >
                          {valueKey[item]}
                        </Typography>
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {Object.values(data.dashboard).map((value: any, index) => (
                    <TableCell key={index}>
                      <Typography variant="body1">{value}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            <>
              <Skeleton animation="wave" width="100%" height="100px" />
            </>
          )}
        </Toolbar>
        <Typography variant="body2">
          Total Users {data?.data?.length}
        </Typography>
        <TableContainer>
          <Table aria-label="simple table" size="small">
            <TableHead>
              <TableRow sx={{ background: theme.palette.action.hover }}>
                <TableCell
                  sx={{ width: "5%", fontWeight: "bold", fontSize: "14px" }}
                  align="left"
                >
                  ID
                </TableCell>
                <TableCell
                  sx={{ width: "15%", fontWeight: "bold", fontSize: "14px" }}
                >
                  Email/Phone
                </TableCell>
                <TableCell
                  sx={{ width: "20%", fontWeight: "bold", fontSize: "14px" }}
                >
                  Registration Date
                </TableCell>
                <TableCell
                  sx={{ width: "35%", fontWeight: "bold", fontSize: "14px" }}
                >
                  Subscription Created at
                </TableCell>
                <TableCell
                  sx={{ width: "15%", fontWeight: "bold", fontSize: "14px" }}
                >
                  Last Login
                </TableCell>
                <TableCell
                  sx={{ width: "10%", fontWeight: "bold", fontSize: "14px" }}
                >
                  Sub End
                </TableCell>
              </TableRow>
            </TableHead>

            {data ? (
              <TableBody>
                {data.data.map((item: Subscriber) => (
                  <SubscribersRow data={item} key={item.user_id} />
                ))}
              </TableBody>
            ) : (
              skeletonArray.map((item, index: number) => (
                <TableRow key={index}>
                  {item.map((el: string[], ind: number) => (
                    <TableCell key={ind} component="th" scope="row">
                      <Skeleton animation="wave" variant="text" height="30px" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default Subscribers;
